// Piano audio playback using Tone.js Sampler with a Salamander Grand Piano
// sample set hosted on the tonejs CDN. Falls back to a synth if samples fail.
import * as Tone from "tone";

let piano = null;
let ready = false;
let playToken = 0;

export function getPlayToken() {
  return ++playToken;
}
export function isCurrentToken(t) {
  return t === playToken;
}

function midiToNoteName(m) {
  const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  const pc = ((m % 12) + 12) % 12;
  const oct = Math.floor(m / 12) - 1;
  return `${names[pc]}${oct}`;
}

async function ensurePiano() {
  if (ready) return piano;
  await Tone.start();
  piano = new Tone.Sampler({
    urls: {
      C4: "C4.mp3",
      "D#4": "Ds4.mp3",
      "F#4": "Fs4.mp3",
      A4: "A4.mp3",
      C5: "C5.mp3",
      "D#5": "Ds5.mp3",
      "F#5": "Fs5.mp3",
      A5: "A5.mp3",
      C6: "C6.mp3",
    },
    release: 1,
    baseUrl: "https://tonejs.github.io/audio/salamander/",
  }).toDestination();
  await Tone.loaded();
  ready = true;
  return piano;
}

// Play a list of MIDI notes.
// mode="chord" -> all at once, "scale" -> arpeggiated at the given BPM (quarter notes).
// onStep(midi | null, index) fires when each note starts, and once with null when done.
export async function playNotes(midis, mode = "chord", bpm = 180, onStep) {
  if (!midis.length) return;
  const token = getPlayToken();
  const inst = await ensurePiano();
  if (!isCurrentToken(token)) return;
  const now = Tone.now() + 0.05;
  const safeStep = (fn, delay) =>
    setTimeout(() => {
      if (isCurrentToken(token)) fn();
    }, delay);
  if (mode === "chord") {
    const names = midis.map(midiToNoteName);
    inst.triggerAttackRelease(names, 1.5, now);
    if (onStep) {
      onStep(midis, 0);
      safeStep(() => onStep(null, -1), 1500);
    }
  } else {
    const step = 60 / bpm;
    midis.forEach((m, i) => {
      inst.triggerAttackRelease(
        midiToNoteName(m),
        step * 0.9,
        now + i * step
      );
      if (onStep) safeStep(() => onStep(m, i), i * step * 1000);
    });
    if (onStep) safeStep(() => onStep(null, -1), midis.length * step * 1000);
  }
}

export function stopAll() {
  playToken++; // invalidate any pending onStep callbacks
  if (piano) {
    try {
      piano.releaseAll();
      piano.disconnect();
      piano.dispose();
    } catch {
      /* ignore */
    }
    piano = null;
    ready = false;
  }
}
